export type FeedPreparationVinTab = 'continued' | 'filteredOut'

export type PreProcessorFilterCriterion = {
  field?: string
  actual?: unknown
  expected?: unknown
}

export type PreProcessorReasonEntry = {
  code?: string
  explanation?: string
  filterLabel?: string
  filterIndex?: number
  matched?: boolean
  criteria?: PreProcessorFilterCriterion[]
}

export type PreProcessorReasonDisplay = {
  summary?: string
  lines: string[]
}

const DISPLAY_VALUE_MAP: Record<string, string> = {
  new: 'New',
  used: 'Used',
  cpo: 'CPO',
}

export const GENERIC_PREPROCESSOR_EXPLANATIONS = new Set([
  "Vehicle did not match this account's shared inventory filters.",
  "Filtered out because it did not match this account's shared inventory filters.",
  'Vehicle was filtered out during Feed Preparation.',
  'Vehicle passed Feed Preparation.',
  'Vehicle continued after Feed Preparation.',
  'Vehicle did not match the group-site source filter for this target account.',
  'Vehicle did not match any configured pre-processor filter.',
])

export function isGenericPreProcessorExplanation(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return true
  if (GENERIC_PREPROCESSOR_EXPLANATIONS.has(trimmed)) return true

  const normalized = trimmed.toLowerCase()
  return (
    normalized.includes('shared inventory filters') ||
    normalized.includes('filtered out during feed preparation') ||
    normalized.includes('continued from feed preparation to primary source check') ||
    normalized.includes('continued after feed preparation') ||
    normalized.includes('group-site source filter')
  )
}

export function cleanPreProcessorReason(raw: string): string {
  return String(raw || '')
    .replace(/^Accepted because it matched shared inventory filter:\s*/i, '')
    .replace(/^Filtered out because\s*/i, '')
    .replace(/^Blocked because\s+/i, '')
    .replace(/^Vehicle did not match the group-site source filter:\s*/i, '')
    .replace(
      /^Vehicle did not match the group-site source filter for this target account\.?\s*/i,
      '',
    )
    .replace(/\.$/, '')
    .trim()
}

export function displayPreProcessorReason(
  raw: string,
  tab: FeedPreparationVinTab,
): string {
  const cleaned = cleanPreProcessorReason(raw)
  if (cleaned && !isGenericPreProcessorExplanation(cleaned)) {
    return cleaned
  }

  return tab === 'continued'
    ? 'Matched Feed Preparation rules'
    : 'Did not match Feed Preparation rules'
}

export function titleCaseField(field: string): string {
  const trimmed = String(field || '').trim()
  if (!trimmed) return 'Field'
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

export function displayValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  const text = String(value).trim()
  if (!text) return '—'
  const mapped = DISPLAY_VALUE_MAP[text.toLowerCase()]
  if (mapped) return mapped
  if (/^[a-z]+$/.test(text)) {
    return text.charAt(0).toUpperCase() + text.slice(1)
  }
  return text
}

function isGroupedSharedInventoryFilterReason(reason: PreProcessorReasonEntry): boolean {
  if (!reason || typeof reason !== 'object') return false
  if (reason.code === 'shared_inventory_filter_mismatch') return true
  if (typeof reason.filterLabel === 'string' && reason.filterLabel.trim().length > 0) return true
  return typeof reason.filterIndex === 'number'
}

export function resolveFilterLabel(reason: PreProcessorReasonEntry): string {
  const explicitLabel = String(reason.filterLabel ?? '').trim()
  if (explicitLabel) return explicitLabel
  if (typeof reason.filterIndex === 'number') {
    return `Filter ${reason.filterIndex + 1}`
  }
  return ''
}

function stripKnownLabelPrefix(text: string, label: string): string {
  if (!label) return text
  const prefix = `${label}:`
  return text.toLowerCase().startsWith(prefix.toLowerCase())
    ? text.slice(prefix.length).trim()
    : text
}

export function cleanFilterExplanation(explanation: string, label = ''): string {
  let cleaned = cleanPreProcessorReason(explanation)
  cleaned = cleaned.replace(/^Matched\s+Filter\s+\d+:\s*/i, '')
  cleaned = stripKnownLabelPrefix(cleaned, label)
  return cleaned.trim()
}

export function formatMissCriterion(criterion: PreProcessorFilterCriterion): string {
  const field = titleCaseField(String(criterion.field ?? 'Field'))
  const actual = displayValue(criterion.actual)
  const expected = displayValue(criterion.expected)
  return `${field} ${actual} → ${expected}`
}

export function parseMissCriteriaFromExplanation(
  explanation: string,
): PreProcessorFilterCriterion[] {
  const cleaned = cleanPreProcessorReason(explanation)
  const criteria: PreProcessorFilterCriterion[] = []

  for (const part of cleaned.split(';')) {
    const trimmed = part.trim()
    if (!trimmed) continue

    const match = trimmed.match(/^(\w+)\s+was\s+(.+?),\s*expected\s+(.+)$/i)
    if (!match) continue

    criteria.push({
      field: match[1],
      actual: match[2].trim(),
      expected: match[3].trim(),
    })
  }

  return criteria
}

function resolveMissCriteria(reason: PreProcessorReasonEntry): PreProcessorFilterCriterion[] {
  if (Array.isArray(reason.criteria) && reason.criteria.length > 0) {
    return reason.criteria
  }
  return parseMissCriteriaFromExplanation(String(reason.explanation ?? ''))
}

export function formatFilterMissLine(reason: PreProcessorReasonEntry): string {
  const label = resolveFilterLabel(reason)
  const criteria = resolveMissCriteria(reason)

  if (criteria.length > 0) {
    return `${label}: ${criteria.map(formatMissCriterion).join('; ')}`
  }

  const explanation = cleanFilterExplanation(String(reason.explanation ?? ''), label)
  return explanation ? `${label}: ${explanation}` : label
}

export function formatMatchedCriteriaLine(
  criteria: PreProcessorFilterCriterion[],
): string {
  return criteria
    .map((criterion) => {
      const field = titleCaseField(String(criterion.field ?? 'Field'))
      const expected = displayValue(criterion.expected ?? criterion.actual)
      return `${field} = ${expected}`
    })
    .join('; ')
}

export function parseMatchedCriteriaFromExplanation(explanation: string): string {
  let criteriaPart = String(explanation ?? '').trim()
  const matchedPrefix = criteriaPart.match(/^Matched\s+Filter\s+\d+:\s*(.+)$/i)
  if (matchedPrefix) {
    criteriaPart = matchedPrefix[1]
  } else {
    const colonIndex = criteriaPart.indexOf(':')
    if (colonIndex >= 0) {
      criteriaPart = criteriaPart.slice(colonIndex + 1).trim()
    }
  }

  if (!criteriaPart) return ''

  const parsedCriteria = criteriaPart
    .split(';')
    .map((part) => part.trim())
    .flatMap((part) => {
      const match = part.match(/^(\w+)\s*=\s*(.+)$/i)
      if (!match) return []
      return [{
        field: match[1],
        expected: match[2].trim(),
      }]
    })

  if (parsedCriteria.length > 0) {
    return formatMatchedCriteriaLine(parsedCriteria)
  }

  return criteriaPart
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(\w+)\s*=\s*(.+)$/i)
      if (!match) return part
      return `${titleCaseField(match[1])} = ${displayValue(match[2].trim())}`
    })
    .join('; ')
}

export function formatMatchedFilterDisplay(
  reason: PreProcessorReasonEntry,
): PreProcessorReasonDisplay {
  const explanation = String(reason.explanation ?? '').trim()
  const matchedFromExplanation = explanation.match(/^Matched\s+(Filter\s+\d+)/i)
  const label = resolveFilterLabel(reason)
  const matchedHeading = matchedFromExplanation
    ? `Matched ${matchedFromExplanation[1]}`
    : label
      ? `Matched ${label}`
      : 'Matched filter'

  const criteriaLine = Array.isArray(reason.criteria) && reason.criteria.length > 0
    ? formatMatchedCriteriaLine(reason.criteria)
    : parseMatchedCriteriaFromExplanation(explanation)

  return {
    lines: criteriaLine ? [matchedHeading, criteriaLine] : [matchedHeading],
  }
}

export function formatPreProcessorReasonDisplay(
  record: {
    status?: string
    reasons?: PreProcessorReasonEntry[]
  },
  tab: FeedPreparationVinTab,
): PreProcessorReasonDisplay {
  const reasons = Array.isArray(record.reasons) ? record.reasons : []
  const grouped = reasons.filter(isGroupedSharedInventoryFilterReason)
  const isSkipped = record.status === 'skipped' || tab === 'filteredOut'
  const isContinued = record.status === 'changed' || tab === 'continued'

  if (grouped.length > 0 && isSkipped) {
    return {
      summary: 'Did not match any filter',
      lines: grouped.map(formatFilterMissLine).filter(Boolean),
    }
  }

  if (grouped.length > 0 && isContinued) {
    return formatMatchedFilterDisplay(grouped[0])
  }

  return {
    lines: [displayPreProcessorReason(selectPreProcessorRawReason(record), tab)].filter(Boolean),
  }
}

export function formatPreProcessorReasonLines(
  record: {
    status?: string
    reasons?: PreProcessorReasonEntry[]
  },
  tab: FeedPreparationVinTab,
): string[] {
  const display = formatPreProcessorReasonDisplay(record, tab)
  return [...(display.summary ? [display.summary] : []), ...display.lines].filter(Boolean)
}

/** @deprecated Prefer formatFilterMissLine / formatPreProcessorReasonDisplay */
export function formatGroupedSharedInventoryReasonLine(
  reason: PreProcessorReasonEntry,
): string {
  return formatFilterMissLine(reason)
}

export function selectPreProcessorRawReason(record: {
  status?: string
  reasons?: PreProcessorReasonEntry[]
}): string {
  const reasons = Array.isArray(record.reasons) ? record.reasons : []
  const groupedFilterReasons = reasons.filter(isGroupedSharedInventoryFilterReason)

  if (groupedFilterReasons.length > 0) {
    const display = formatPreProcessorReasonDisplay(record, record.status === 'skipped' ? 'filteredOut' : 'continued')
    const combined = [...(display.summary ? [display.summary] : []), ...display.lines]
      .filter(Boolean)
      .join('\n')
    if (combined) return combined
  }

  const explanations = reasons
    .map((reason) => (typeof reason.explanation === 'string' ? reason.explanation.trim() : ''))
    .filter((text) => text.length > 0)

  const specific = explanations.find((text) => !isGenericPreProcessorExplanation(text))
  if (specific) return specific

  if (explanations.length > 0) return explanations[0]

  return record.status === 'skipped'
    ? 'Vehicle was filtered out during Feed Preparation.'
    : 'Vehicle passed Feed Preparation.'
}
